import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box, Menu, MenuItem, Typography, CircularProgress } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { toast } from 'react-toastify';
import http from '../../services/http';

/**
 * A fase do relacionamento, mudável na hora.
 *
 * ## Por que existe
 *
 * O campo nasceu (item 7) editável só pelo formulário completo do cliente.
 * Pedido do dono do produto em 05/10: a informação chega **durante** o
 * atendimento -- "esse cliente saiu do treinamento, agora é suporte" --, e
 * obrigar a sair do chamado para registrar é o atrito que faz o campo não ser
 * preenchido.
 *
 * ## Menu, e não `<select>` aberto
 *
 * Num cartão denso, um select sempre ativo muda dado de cliente com um scroll
 * ou uma tecla sem querer. O menu exige a intenção: um clique para abrir,
 * outro para escolher -- ainda muito mais rápido que o formulário.
 *
 * ## A permissão
 *
 * `#editar-fase-relacionamento`, própria e separada de "editar dados do
 * cliente" -- decisão do dono do produto: quem define a fase (comercial,
 * treinamento) não é necessariamente quem edita cadastro. Sem ela o valor é
 * só texto, sem nada clicável: um controle que parece editável e responde 403
 * é pior que nenhum controle.
 */
export const PERMISSAO_EDITAR_FASE = '#editar-fase-relacionamento';

/**
 * As fases disponíveis, buscadas UMA vez por sessão.
 *
 * A lista mora na api (`Company::FASES_DO_RELACIONAMENTO`), e não aqui: com
 * uma cópia no front, a primeira fase nova faria a tela oferecer uma opção que
 * a api recusa com 422 -- ou o contrário.
 *
 * A promessa fica em cache de módulo porque este controle aparece em duas
 * telas, e o cartão do chamado o repete por cliente vinculado: sem o cache
 * seriam N requisições para a mesma lista de três itens, que praticamente
 * nunca muda.
 */
let promessaDasFases = null;

/**
 * A cor de cada fase, para a variante `destaque`.
 *
 * ## Por que o destaque existe
 *
 * Pedido da Gisele (3º bloco). No cartão do chamado a fase é um campo de 13px
 * entre Código, Endereço, Telefone e mais meia dúzia — ela some. E ela é a
 * informação que muda COMO se atende: quem está em treinamento não recebe a
 * mesma resposta de quem está em suporte. Um dado que decide o atendimento não
 * pode ter o mesmo peso visual de um CEP.
 *
 * ## Nomes de paleta, nunca hex
 *
 * `primary` é `#1e4d72` no tema claro e `#63ace5` no escuro -- um hex fixo
 * aqui ficaria ilegível em um dos dois, que é exatamente o cuidado que a nota
 * do `currentColor` mais abaixo já registrava.
 *
 * ## Fase sem cor mapeada cai no cinza, e não numa cor emprestada
 *
 * A lista de fases mora na api (`Company::FASES_DO_RELACIONAMENTO`), então uma
 * fase nova chega aqui sem passar por este arquivo. Herdar a cor de outra fase
 * faria a tela afirmar um significado que ninguém escolheu; o cinza diz
 * "existe, e ainda não tem cor".
 */
const PALETA_DA_FASE = {
    comercial: 'primary',
    treinamento: 'warning',
    suporte: 'success',
};

export const useFasesDoRelacionamento = () => {
    const [fases, setFases] = useState([]);

    useEffect(() => {
        let cancelado = false;

        if (!promessaDasFases) {
            promessaDasFases = http.get('/companies/relationship-phases')
                .then(({ data }) => (Array.isArray(data) ? data : []))
                // Falha NÃO fica em cache: um erro de rede momentâneo não deve
                // deixar o seletor vazio pelo resto da sessão.
                .catch(() => { promessaDasFases = null; return []; });
        }

        promessaDasFases.then((lista) => {
            if (!cancelado) setFases(lista);
        });

        return () => { cancelado = true; };
    }, []);

    return fases;
};

const FaseDoRelacionamento = ({
    empresa,
    fases,
    onMudou,
    variante = 'texto',
    semValor = 'Não definida',
}) => {
    const [ancora, setAncora] = useState(null);
    const [salvando, setSalvando] = useState(false);
    // O valor que esta tela mostra AGORA.
    //
    // Existe porque as duas telas que usam este controle guardam a empresa de
    // formas diferentes -- no cartão do chamado ela é estado local, no
    // cabeçalho do chat vem de cima. Guardar aqui o que acabou de ser salvo
    // evita propagar para cima em uma delas, e na próxima carga o valor vem
    // do servidor de qualquer jeito.
    const [recemSalvo, setRecemSalvo] = useState(null);

    const permissoes = useSelector((s) => s.user?.permissions) || [];
    const usuario = useSelector((s) => s.user?.currentUser);
    const podeEditar = Boolean(usuario?.is_super_admin)
        || (Array.isArray(permissoes) && permissoes.includes(PERMISSAO_EDITAR_FASE));

    const rotulo = recemSalvo !== null
        ? (recemSalvo.label || semValor)
        : (empresa?.relationship_phase_label || semValor);
    const valorAtual = recemSalvo !== null
        ? recemSalvo.value
        : (empresa?.relationship_phase || '');

    const escolher = async (valor) => {
        setAncora(null);
        if (valor === valorAtual || !empresa?.id) return;

        setSalvando(true);
        try {
            const { data } = await http.put(`/companies/${empresa.id}/relationship-phase`, {
                relationship_phase: valor || null,
            });
            setRecemSalvo({
                value: data?.relationship_phase || '',
                label: data?.relationship_phase_label || null,
            });
            if (onMudou) onMudou(data);
        } catch (err) {
            // Avisa, e NÃO muda o que está na tela. Um controle que mostra o
            // valor novo e não salvou é pior que um erro: o operador sai
            // achando que registrou.
            toast.error(
                err?.response?.data?.message
                || 'Não foi possível mudar a fase do relacionamento.'
            );
        } finally {
            setSalvando(false);
        }
    };

    const conteudo = (
        <>
            {rotulo}
            {salvando && <CircularProgress size={11} sx={{ ml: 0.75 }} />}
        </>
    );

    /*
     * O destaque: pílula com a cor da fase.
     *
     * `alpha()` sobre a cor da própria paleta em vez de um cinza-claro fixo --
     * assim o fundo acompanha o tema sozinho, e no escuro ele clareia em vez
     * de virar uma mancha.
     *
     * Sem fase definida o fundo é mais fraco (0.08) e a cor é o cinza de texto
     * desabilitado: "não definida" não pode parecer um status conquistado.
     */
    const paletaDaFase = PALETA_DA_FASE[valorAtual] || null;
    const corDaFase = (t) => (paletaDaFase ? t.palette[paletaDaFase].main : t.palette.text.disabled);

    const estiloDoDestaque = variante === 'destaque' ? {
        display: 'inline-flex',
        alignItems: 'center',
        px: 0.75,
        py: 0.125,
        borderRadius: 1,
        fontSize: '11.5px',
        fontWeight: 600,
        letterSpacing: 0.2,
        lineHeight: 1.7,
        whiteSpace: 'nowrap',
        color: corDaFase,
        backgroundColor: (t) => alpha(corDaFase(t), paletaDaFase ? 0.14 : 0.08),
        border: '1px solid',
        borderColor: (t) => alpha(corDaFase(t), 0.35),
    } : null;

    if (!podeEditar) {
        if (!estiloDoDestaque) {
            return <>{conteudo}</>;
        }

        // Sem permissão a pílula é a MESMA, só com a borda sólida: o destaque
        // é informação, e quem não pode editar precisa dela igual.
        return (
            <Box component="span" data-testid="fase-do-relacionamento-destaque" sx={estiloDoDestaque}>
                {conteudo}
            </Box>
        );
    }

    return (
        <>
            <Box
                component="span"
                role="button"
                tabIndex={0}
                data-testid="fase-do-relacionamento"
                data-destaque={variante === 'destaque' ? 'sim' : undefined}
                aria-label="Mudar a fase do relacionamento"
                onClick={(e) => setAncora(e.currentTarget)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setAncora(e.currentTarget);
                    }
                }}
                sx={{
                    cursor: 'pointer',
                    opacity: salvando ? 0.6 : 1,
                    '&:hover': { opacity: 0.75 },
                    ...(estiloDoDestaque
                        // Na pílula o tracejado vai na borda INTEIRA, e não só
                        // embaixo: continua sendo o mesmo sinal de "isto se
                        // edita", mas um sublinhado dentro de uma caixa com
                        // borda sólida ficaria lendo como dois controles.
                        ? { ...estiloDoDestaque, borderStyle: 'dashed' }
                        : {
                            borderBottom: '1px dashed',
                            // `currentColor` de proposito: este controle aparece em
                            // dois lugares com cores de texto diferentes, e fixar uma
                            // cor aqui quebraria num dos dois -- ou num dos temas.
                            borderColor: 'currentColor',
                        }),
                }}
            >
                {conteudo}
            </Box>
            <Menu anchorEl={ancora} open={Boolean(ancora)} onClose={() => setAncora(null)}>
                {/*
                  "Não definida" é opção de verdade, e não placeholder: é como
                  o operador desfaz uma escolha errada sem abrir o cadastro.
                */}
                <MenuItem onClick={() => escolher('')} selected={valorAtual === ''}>
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{semValor}</Typography>
                </MenuItem>
                {(fases || []).map((f) => (
                    <MenuItem key={f.value} onClick={() => escolher(f.value)} selected={valorAtual === f.value}>
                        <Typography variant="body2">{f.label}</Typography>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default FaseDoRelacionamento;
